/**
 * #37124: the additional-insured forms the prior carrier's dec listed for an imported interested party.
 *
 * GO lists, for each additional insured it names, the form in that AI's `InterestFormCd` (GL-70, GL-71,
 * GL-72, GL-108) whatever it charges for them and whatever the liability type, and no form for an
 * Additional Named Insured count it charges without naming anyone. Branch derives the form from the
 * party's `relationship`, but on an imported term the relationship is chosen to make the CFM rater's
 * AIRP / AINR / AILT counts equal GO's (#36943): a GL-71 LLC that GO charges as a trust is a
 * `trustCorpsEtc`, a party GO names without charging is a non-rated `additionalInterest`, a GL-71 on
 * Commercial liability is a GL-108, and a count with no name is a placeholder party. So the importer
 * records GO's form, and the relationship it imported the party as, on `importedForms`; the farm dec
 * mapper prints exactly those forms while the party keeps that relationship. Once staff reclassify the
 * party the record no longer applies and the relationship decides again.
 *
 * Rating reads the record only on a party marked `countedByPriorCarrier`, i.e. one the importer left
 * holding one of GO's counts (or a placeholder for a count GO named nobody for). Its relationship was chosen
 * to fill that count — a GL-70 can be an `occupant`, a Commercial GL-71 a GL-108 — so per AAIS it is charged
 * only when its `formNumbers` hold a premium-bearing GL-71/72 (#37202, adversarial review on #37226). On
 * every other party the forms a dec prints cannot move a premium. See `countCfmAdditionalNamedInsureds`.
 *
 * The record is server-owned: only the importer writes it. It is not on any GraphQL input type, and every
 * save path that replaces `farmInterestedParties` (policy-mod endorsements, rater-v2 offer updates) runs
 * `enforceImportedPartyForms` to restore it from the stored party (adversarial review on #37127).
 */

/** The additional-insured party forms an import record governs. Every other party form is derived. */
export const IMPORTED_ADDITIONAL_INSURED_FORMS: readonly string[] = ['GL-70', 'GL-71', 'GL-72', 'GL-108'];

export interface ImportedPartyForms {
  /** The relationship the party was imported as. */
  relationship?: string | null;
  /** The additional-insured forms the prior carrier listed for the party; empty when it listed none. */
  formNumbers?: (string | null)[] | null;
  /**
   * The party holds one of the Additional Named Insured counts the prior carrier charged (GO's
   * `ResidingAICnt` / `NotResidingAICnt` / `TrustCorpsAICnt`), whatever relationship carries it, or is a
   * placeholder for a count GO named nobody for. Only the importer sets it. Such a party is charged only
   * with a GL-71/72 in `formNumbers` (#37202).
   */
  countedByPriorCarrier?: boolean | null;
}

export interface PartyWithImportedForms {
  relationship?: string | null;
  name?: string | null;
  address?: unknown;
  importedForms?: ImportedPartyForms | null;
}

/**
 * The name the GO importer gives a placeholder for an Additional Named Insured count GO charged without naming
 * anyone, by the count it fills. `index` is 0-based; the name numbers from 1.
 */
const IMPORTED_PLACEHOLDER_LABELS = {
  insuredResidents: 'Additional Named Insured (residing)',
  insuredPartners: 'Additional Named Insured (not residing)',
  trustCorps: 'Additional Named Insured (trust/corporation)'
} as const;

export type ImportedPlaceholderBucket = keyof typeof IMPORTED_PLACEHOLDER_LABELS;

export function importedPlaceholderPartyLabel(bucket: ImportedPlaceholderBucket): string {
  return IMPORTED_PLACEHOLDER_LABELS[bucket];
}

export function importedPlaceholderPartyName(bucket: ImportedPlaceholderBucket, index: number): string {
  return `${IMPORTED_PLACEHOLDER_LABELS[bucket]} ${index + 1}`;
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const IMPORTED_PLACEHOLDER_NAME = new RegExp(
  `^(${Object.values(IMPORTED_PLACEHOLDER_LABELS).map(escapeRegExp).join('|')}) \\d+$`
);

/**
 * A party the GO importer made up for a count GO charged without naming anyone (#37705): it holds the count, GO
 * listed no form for it, and it still has the importer's placeholder name. A party GO named is never one, even
 * when GO listed no form for it.
 */
export function isImportedPlaceholderParty(party: PartyWithImportedForms | null | undefined): boolean {
  const record = party?.importedForms;
  return (
    !!record?.countedByPriorCarrier &&
    (record.formNumbers ?? []).length === 0 &&
    IMPORTED_PLACEHOLDER_NAME.test((party?.name ?? '').trim())
  );
}

/** Any non-blank value in an address (staff-fe sends an address of empty strings for "none"). */
function hasAddress(address: unknown): boolean {
  if (address == null) {
    return false;
  }
  if (typeof address === 'string') {
    return address.trim() !== '';
  }
  if (typeof address === 'object') {
    return Object.values(address as Record<string, unknown>).some(hasAddress);
  }
  return true;
}

/**
 * Staff identified a placeholder in place (#37705): gave it a name other than the importer's, or an address it
 * did not have. It is now the real party, so its import record no longer describes it.
 */
function isIdentifiedPlaceholder(
  party: PartyWithImportedForms,
  storedParty: PartyWithImportedForms | null | undefined
): boolean {
  if (!isImportedPlaceholderParty(storedParty)) {
    return false;
  }
  const renamed = (party.name ?? '').trim() !== (storedParty?.name ?? '').trim();
  const addressAdded = hasAddress(party.address) && !hasAddress(storedParty?.address);
  return renamed || addressAdded;
}

/**
 * The additional-insured forms (GL-70/71/72/108) the dec lists for `party` from its import record, or
 * null when the forms come from its relationship: no record (a party written in Branch), or staff have
 * since changed the relationship. An empty list means the prior carrier listed none for the party.
 */
export function importedAdditionalInsuredForms(party: PartyWithImportedForms | null | undefined): string[] | null {
  const imported = party?.importedForms;
  if (!imported || imported.relationship !== party?.relationship) {
    return null;
  }
  return (imported.formNumbers ?? []).filter(
    (formNumber): formNumber is string => !!formNumber && IMPORTED_ADDITIONAL_INSURED_FORMS.includes(formNumber)
  );
}

/**
 * What a spent record is overwritten with. Segments store an endorsement as a delta, and the merge
 * (`makePolicyDetailsFromSegments`) never removes a key, so deleting the record would leave the stored
 * one in every later merged read — and changing the relationship back would match it again, reviving the
 * forms and, through `countedByPriorCarrier`, the party's charge. An empty relationship matches no party,
 * so every reader treats it as spent; `countedByPriorCarrier: false` overwrites a stored `true` (a delta
 * writes a removed key as nothing at all). devlo review on #37151
 */
const SPENT_IMPORTED_PARTY_FORMS: Readonly<ImportedPartyForms> = {
  relationship: '',
  formNumbers: [],
  countedByPriorCarrier: false
};

/**
 * Keeps `importedForms` server-owned on a save that replaces `farmInterestedParties` (#37124, adversarial
 * review on #37127). Mutates `parties` in place, matching each to the stored party by id:
 *   - no stored record (a new party, or one the import never stamped): any record the caller sent is
 *     dropped (`forged`), so a caller can never create one or pick its forms;
 *   - a stored record and the party still has the relationship it records: the stored record is restored,
 *     whatever the caller sent (so editing its forms or `countedByPriorCarrier` does nothing);
 *   - a stored record but the relationship changed: the record is overwritten with a spent one (`spent`),
 *     so the relationship rule applies from then on and changing the relationship back does not revive it;
 *   - a placeholder staff identified in place (renamed, or given an address; #37705) is spent the same way,
 *     so it prints and is charged by its relationship like a party added in Ember Tech;
 *   - a stored spent record stays spent.
 */
export function enforceImportedPartyForms<T extends PartyWithImportedForms & { id?: string | null }>(
  parties: (T | null | undefined)[] | null | undefined,
  storedParties: (T | null | undefined)[] | null | undefined
): { forged: number; spent: number } {
  const result = { forged: 0, spent: 0 };
  for (const party of parties ?? []) {
    if (!party) {
      continue;
    }
    const stored = (storedParties ?? []).find((candidate) => !!candidate?.id && candidate.id === party.id);
    const storedRecord = stored?.importedForms;
    if (!storedRecord) {
      if (party.importedForms != null) {
        result.forged += 1;
      }
      delete party.importedForms;
      continue;
    }
    if (
      storedRecord.relationship &&
      party.relationship === storedRecord.relationship &&
      !isIdentifiedPlaceholder(party, stored)
    ) {
      party.importedForms = {
        relationship: storedRecord.relationship,
        formNumbers: [...(storedRecord.formNumbers ?? [])],
        ...(storedRecord.countedByPriorCarrier ? { countedByPriorCarrier: true } : {})
      };
      continue;
    }
    if (storedRecord.relationship) {
      result.spent += 1;
    }
    party.importedForms = { ...SPENT_IMPORTED_PARTY_FORMS, formNumbers: [] };
  }
  return result;
}
